import './Input.scss';
import React, { CSSProperties, useState, useEffect, ChangeEvent } from 'react';
import classNames from 'classnames';
import Tooltip from '@/components/basic/Tooltip/Tooltip';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
        type?: 'text' | 'number' | 'password' | 'email' | 'textarea';
        tooltip?: string;
        label?: string;
        required?: boolean;
        placeholder?: string;
        className?: string;
        labelWidth?: string | number | boolean;
        style?: CSSProperties;
        error?: string;
        isSeoDescription?: boolean;
    };

const Input = React.forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    InputProps
>(
    (
        {
            type = 'text',
            label,
            placeholder,
            className,
            labelWidth,
            style,
            error,
            maxLength,
            isSeoDescription,
            value,
            defaultValue,
            onChange,
            ...props
        },
        ref,
    ) => {
        const [charCount, setCharCount] = useState(
            (value || defaultValue || '').toString().length,
        );

        useEffect(() => {
            if (value !== undefined) {
                setCharCount(String(value).length);
            }
        }, [value]);

        const isTextArea = type === 'textarea';
        const Tag = (isTextArea ? 'textarea' : 'input') as any;

        const derivedPlaceholder =
            placeholder !== undefined
                ? placeholder
                : isSeoDescription
                  ? 'Die ersten 150 Zeichen sind SEO-entscheidend'
                  : label;

        const handleInputChange = (
            e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            setCharCount(e.target.value.length);
            if (onChange) {
                onChange(e);
            }
        };

        const customStyle = {
            ...style,
            ...(labelWidth && labelWidth !== true
                ? {
                      '--label-width':
                          typeof labelWidth === 'number'
                              ? `${labelWidth}px`
                              : labelWidth,
                  }
                : {}),
        } as CSSProperties;

        const warnThreshold = isSeoDescription
            ? 155
            : maxLength
              ? maxLength * 0.9
              : 0;
        const counterElement = maxLength && (
            <div
                className={classNames('Input_maxLength', {
                    isWarning:
                        maxLength &&
                        charCount >= warnThreshold &&
                        charCount < maxLength,
                    isFull: maxLength && charCount >= maxLength,
                    'Input_maxLength-textarea': isTextArea,
                })}
            >
                {charCount} / {maxLength}
            </div>
        );

        const renderInputBody = (
            <Tag
                {...props}
                ref={ref}
                value={value}
                defaultValue={defaultValue}
                maxLength={maxLength}
                onChange={handleInputChange}
                type={isTextArea ? undefined : type}
                placeholder={derivedPlaceholder}
                aria-invalid={error ? 'true' : 'false'}
                className={classNames('Input', {
                    'Input-textarea': isTextArea,
                    isInvalid: !!error,
                })}
            />
        );

        const renderInput = (
            <label
                className={classNames(
                    'Input_container',
                    {
                        'Input_container-textarea': isTextArea,
                        hasLabelWidth: !!labelWidth,
                    },
                    className,
                )}
                style={customStyle}
            >
                {label && <span className="Input_label">{label}</span>}

                <div
                    className={classNames('Input_fieldWrapper', {
                        hasLabel: label,
                    })}
                >
                    {renderInputBody}
                    {counterElement}
                    <div
                        className={classNames('Input_error', {
                            isInvalid: !!error,
                        })}
                    >
                        {error || '\u00a0'}
                    </div>
                </div>
            </label>
        );

        return props.tooltip ? (
            <Tooltip content={props.tooltip}>{renderInput}</Tooltip>
        ) : (
            renderInput
        );
    },
);

Input.displayName = 'Input';

export default Input;
